/**
 * Master Data Edit (listener)
 * Binds UI events (buttons + dropdown) with EventScope.
 */

import { disposeEventScope, getEventScope } from "../core/event-scope/event-scope";
import { MasterDataEditRender } from "./master-data-edit-render";
import { Roles } from "../constants/roles";
import { MasterDataEditController } from "./master-data-edit-handler";
import { getClosest, getEventTarget } from "./master-data-edit-form";

type ControllerMap = Map<string, MasterDataEditController>;

export class MasterDataEditListener {
    public register(controllers: ControllerMap): void {
        disposeEventScope(document, "master-data-edit");
        const scope = getEventScope(document, "master-data-edit");

        const resolveController = (element: Element): MasterDataEditController | null => {
            const keyed = getClosest(element, "[data-master-editor]");
            const key = keyed?.getAttribute("data-master-editor") || "";
            if (key && controllers.has(key)) {
                return controllers.get(key) || null;
            }
            if (controllers.size === 1) {
                return controllers.values().next().value || null;
            }
            return null;
        };

        scope.delegate("click", ".master-menu-btn", (event: Event, button: Element) => {
            event.preventDefault();
            event.stopPropagation();
            const dropdown = getClosest(button, ".master-dropdown");
            if (!(dropdown instanceof HTMLElement)) return;
            MasterDataEditRender.toggleMasterMenu(dropdown);
        });

        scope.delegate("click", ".master-menu .menu-item", () => {
            MasterDataEditRender.closeAllMasterMenus();
        });

        scope.delegate("click", `[data-role="${Roles.MASTER_EDIT_OPEN}"]`, (event: Event, element: Element) => {
            event.preventDefault();
            const controller = resolveController(element);
            controller?.openEdit();
        });

        scope.delegate("click", `[data-role="${Roles.MASTER_ADD_OPEN}"]`, (event: Event, element: Element) => {
            event.preventDefault();
            const controller = resolveController(element);
            controller?.openCreate();
        });

        scope.delegate("click", `[data-role="${Roles.MASTER_DELETE}"]`, async (event: Event, element: Element) => {
            event.preventDefault();
            const controller = resolveController(element);
            if (!controller) return;
            await controller.destroy();
        });

        scope.on("click", (event: Event) => {
            const target = getEventTarget(event);
            if (!getClosest(target, ".master-dropdown")) {
                MasterDataEditRender.closeAllMasterMenus();
            }
        });

        scope.on("keydown", (event: Event) => {
            if (event instanceof KeyboardEvent && event.key === "Escape") {
                MasterDataEditRender.closeAllMasterMenus();
            }
        });
    }

    public initialize(controllers: ControllerMap): void {
        this.register(controllers);
    }
}
